import { action, computed, observable, runInAction } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { format } from 'date-fns'
import { groupBy, map, sortBy } from 'lodash-es'
import { type TimeRange } from '@/infra/time-range'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { type Consultation } from '@/features/jobs/consultations/Consultation'
import { Member } from '@/features/iam/members/Member'
import { User } from '@/features/iam/users/User'
import type { AvatarSubject } from '@/components/Avatar'

/**
 * A day and its timeslots for which the pro can book a consultation
 * based on the client's availability.
 */
interface ScheduledDay {
  formattedDay: string
  timeSlots: TimeRange[]
}

/**
 * Information used for creating a consultation and prompting the user.
 */
export interface ConsultationDetails {
  requestedLength: number
  availability: TimeRange[] | undefined
  bookedConsultations: Consultation[]
  client: User
}

/**
 * Consultation details.
 */
const ConsultationDetails: { none: ConsultationDetails } = {
  /**
   * An empty details "instance".
   */
  none: {
    requestedLength: 30,
    availability: [],
    bookedConsultations: [],
    client: User.none,
  },
}

/**
 * View Model for the Consultation Modal.
 */
export class ConsultationModalViewModel {
  readonly modal = new ModalViewModel()

  /**
   * The time slot selected by the pro. This is one of the time slots
   * allowable based on the customer's specified availability.
   */
  @observable
  accessor selectedTimeSlot: TimeRange | null = null

  /**
   * The days and the time slots within them for which the pro can book
   * the consultation based on the client's availability.
   */
  @observable
  accessor availableDays: ScheduledDay[] = []

  /**
   * Details for the consultation.
   */
  @observable private accessor _consultationDetails: ConsultationDetails =
    ConsultationDetails.none

  /**
   * The current member (likely the pro picking up the job).
   */
  @observable.ref private accessor _currentMember: Member = Member.none

  /**
   * The provider's selection.
   */
  private readonly _prompt = new AsyncPrompt<TimeRange>({
    abort$: this.modal.close$,
  })

  /**
   * The customer's requested length in minutes for the Consultation.
   */
  @computed
  get requestedLength() {
    return this._consultationDetails.requestedLength
  }

  /**
   * The customer's availability for the Consultation.
   */
  @computed
  get availability() {
    return this._consultationDetails.availability
  }

  /**
   * List of booked consultations.
   */
  @computed
  get bookedConsultations() {
    return this._consultationDetails.bookedConsultations
  }

  /**
   * The client's avatar.
   */
  @computed
  get clientAvatar(): AvatarSubject {
    const client = this._consultationDetails.client
    return {
      avatar: client.avatar,
      avatarFallback: client.avatarFallback,
      displayName: client.displayName,
    }
  }

  /**
   * The avatar of the current member (likely the pro picking up the job).
   */
  @computed
  get currentMemberAvatar(): AvatarSubject {
    return {
      avatar: this._currentMember.avatar,
      avatarFallback: this._currentMember.avatarFallback,
      displayName: this._currentMember.displayName,
    }
  }

  /**
   * The days to pick a time slot from, with each slot's booked and selected
   * state.
   */
  @computed
  get days() {
    return this.availableDays.map((d) => ({
      label: d.formattedDay,
      slots: d.timeSlots.map((range) => {
        const isBooked = this.bookedConsultations.some((c) =>
          c.bookedTime?.overlaps(range),
        )
        return {
          startTime: range.startTime,
          endTime: range.endTime,
          isBooked,
          selected: !!this.selectedTimeSlot?.isEqual(range) || isBooked,
          onSelect: () => this.selectTimeSlot(range),
        }
      }),
    }))
  }

  /**
   * Inflates the Modal.
   */
  async promptForTimeSlots(
    consultationDetails: ConsultationDetails,
    currentMember: Member,
  ) {
    this.modal.close()

    const responsePromise = this._prompt.prompt()

    runInAction(() => {
      this._consultationDetails = consultationDetails
      this._currentMember = currentMember
      this.selectedTimeSlot = null
      this.availableDays = this._computeScheduledDays(this.availability ?? [])
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response
  }

  /**
   * Closes the modal without a selected time slot.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Selects the time slot.
   */
  @action.bound
  selectTimeSlot(range: TimeRange) {
    this.selectedTimeSlot = range
  }

  /**
   * Submits the selected time slot.
   */
  @action.bound
  submit() {
    if (!this.selectedTimeSlot) {
      return
    }

    this._prompt.respond(this.selectedTimeSlot)
    this.close()
  }

  /**
   * Groups the customer's availability by day.
   */
  private _computeScheduledDays(ranges: TimeRange[]) {
    const sorted = sortBy(ranges, 'startTime')
    const grouped = groupBy(sorted, (t) => format(t.startTime, 'EEEE, d MMMM'))

    return map(grouped, (timeSlots, formattedDay) => {
      return { formattedDay, timeSlots }
    })
  }
}
